import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import SaveButton from '../../../components/Buttons/SaveButton';
import FileUploadField from '../../../components/Fields/FileUploadField';
import SelectListField from '../../../components/Fields/SelectListField';
import SwitchField from '../../../components/Fields/SwitchField';
import TextboxField from '../../../components/Fields/TextboxField';
import { FormConfig } from '../../../components/Fields/Types';
import useSelectListOptions from '../../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../../components/Hooks/ValidationHook/useValidation';
import Popup from '../../../components/Popups/Popup';
import { ApplicationFunctions, useAuth } from '../../../contexts/authContext';
import { useNotifications } from '../../../contexts/notificationContext';
import DocumentTemplateModel from '../../../models/DocumentTemplateModel';
import FileCreateResultModel from '../../../models/FileCreateResultModel';
import DataService from '../../../services/data.service';
import IdParams from '../../../types/IdParams';
import { PopupProps } from '../../../types/PopupProps';
import { DataResult } from '../../../types/Result';
import Unauthorized from '../../Authentication/Unauthorized';

interface DocumentTemplateCreateModel {
    name: string
    attachmentTypeId: number
    language: string
    documentOutputTypeId: number
    latestVersion: number
    isActive: boolean
    emailSubject: string

    fileName?: string
    file ?: File
    fileResult ?: FileCreateResultModel
}

const CreateDocumentTemplatePopup: React.FC<PopupProps> = ({ show, onHide }) => {
    const attachmentTypes = useSelectListOptions('api/v1/selectlist/AttachmentTypes', '-- Select attachement type');
    const languages = useSelectListOptions('api/v1/selectlist/Languages', '-- Select language');
    const outputTypes = useSelectListOptions('api/v1/selectlist/DocumentOutputTypes', '-- Select output type');
    const [file, setFile] = useState<File>();
    const [serverErrors, setServerErrors] = useState<DataResult<DocumentTemplateCreateModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();

    const [formData, setFormData] = useState<DocumentTemplateCreateModel>({
        name: '',
        attachmentTypeId: 0,
        language: '',
        documentOutputTypeId: 0,
        latestVersion: 1,
        isActive: true,
        emailSubject: '',
    } as DocumentTemplateModel);

    let idParams: IdParams = useParams();
    let documentTemplateId: number = 0;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        documentTemplateId = parseInt(idParams.id);
    }

    function submit() {
        if (formData && file) {
            DataService
                .createFile(file)
                .then(fileResponse => {
                    handleResult(fileResponse);
                    if (fileResponse.isSuccesful === true && fileResponse.data) {
                        formData.fileResult = fileResponse.data;
                        DataService
                            .create<DocumentTemplateCreateModel>(`api/v1/documenttemplates/`, formData)
                            .then(response => {
                                handleResult(response);
                                setServerErrors(response);
                                if (response.isSuccesful === true && response.data)
                                    setFormData(response.data);
                            })
                            .catch(e => handleException(e));
                    }
                })
        }
    }
        
    function onFieldChanged(name: string, value: string | File): void {
        if (name === 'fileName' && typeof (value) === 'object') {
            setFile(value);
            formData.fileName = value.name;
        }
        else {
            formData[name] = value;
        }
        setFormData(formData);
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'emailSubject', label: 'Email subject' },
            { name: 'attachmentTypeId', label: 'Attachment type', validationRules: [{ type: 'isRequired' }] },
            { name: 'language', label: 'Language', validationRules: [{ type: 'isRequired' }] },
            { name: 'documentOutputTypeId', label: 'Output type', validationRules: [{ type: 'isRequired' }] },
            { name: 'isActive', label: 'Is active' },
            { name: 'fileName', label: 'Template' },
        ],
        handleSubmit: submit,
        handleFieldChanged: onFieldChanged,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedFor } = useAuth();
    if (!isAuthorizedFor(ApplicationFunctions.EditDocumentTemplates)) {
        return (<Unauthorized />);
    }

    return (
        <Popup show={show} onHide={onHide} size={'lg'} title={'Create document template'} >
            <Popup.Body>
                <TextboxField {...getFieldProps('name')} />
                <TextboxField {...getFieldProps('emailSubject')} />
                <SelectListField {...getFieldProps('attachmentTypeId')} options={attachmentTypes} />
                <SelectListField {...getFieldProps('language')} options={languages} />
                <SelectListField {...getFieldProps('documentOutputTypeId')} options={outputTypes} />
                <FileUploadField {...getFieldProps('fileName')} />
            </Popup.Body>
            <Popup.Footer>
                <SaveButton {...getSubmitButtonProps()} text='Add' />
            </Popup.Footer>
        </Popup>
    );
}
export default CreateDocumentTemplatePopup;